import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import ReactCrop from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";

// 페이지 애니메이션 설정
const pageVariants = {
  initial: {
    opacity: 0,
  },
  animate: {
    opacity: 1,
    transition: {
      duration: 0.3,
    },
  },
  exit: {
    opacity: 0,
    transition: {
      duration: 0.2,
    },
  },
};

function IdCardCropPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [originalImage, setOriginalImage] = useState(null);
  const [crop, setCrop] = useState({
    unit: "%",
    width: 80,
    height: 50,
    x: 10,
    y: 25,
    aspect: 325 / 208, // 신분증 가로세로 비율 (IdCardCameraPage에서 사용하는 비율)
  });
  const [completedCrop, setCompletedCrop] = useState(null);
  const imgRef = useRef(null);
  const previewCanvasRef = useRef(null);

  useEffect(() => {
    // location.state에서 이미지 가져오기 또는 로컬 스토리지에서 가져오기
    const image = location.state?.originalImage;
    if (image) {
      setOriginalImage(image);
    } else {
      // 이미지가 없는 경우 업로드 페이지로 돌아가기
      navigate("/id-card-submit");
    }
  }, [location, navigate]);

  // 크롭된 이미지 생성 함수
  const generateCroppedImage = () => {
    if (!completedCrop || !imgRef.current) return;

    const image = imgRef.current;
    const canvas = document.createElement("canvas");
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    canvas.width = 325; // 최종 출력 너비
    canvas.height = 208; // 최종 출력 높이

    const ctx = canvas.getContext("2d");

    ctx.drawImage(
      image,
      completedCrop.x * scaleX,
      completedCrop.y * scaleY,
      completedCrop.width * scaleX,
      completedCrop.height * scaleY,
      0,
      0,
      canvas.width,
      canvas.height
    );

    return canvas.toDataURL("image/jpeg", 0.95);
  };

  // 완료 버튼 클릭 시
  const handleCropComplete = () => {
    const croppedImage = generateCroppedImage();
    if (croppedImage) {
      // 로컬 스토리지에 저장
      localStorage.setItem("temp_captured_image", croppedImage);

      // IdCardSubmitPage로 이동
      navigate("/id-card-submit", {
        state: {
          fromCrop: true,
          capturedImage: { imageDataURL: croppedImage },
        },
      });
    }
  };

  // 취소 버튼 클릭 시
  const handleCancel = () => {
    navigate(-1);
  };

  return (
    <motion.div
      className="crop-page"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
    >
      <div className="crop-header">
        <button className="crop-back-button" onClick={handleCancel}>
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M15 18L9 12L15 6"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <h1>신분증 영역 설정</h1>
        <button className="crop-close-button" onClick={handleCancel}>
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M18 6L6 18"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M6 6L18 18"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>

      <div className="crop-container">
        <div className="crop-instructions">
          <p>
            신분증이 <span className="crop-accent">프레임 안에 완전히</span>{" "}
            들어오도록 조정해주세요
          </p>
        </div>

        {originalImage && (
          <ReactCrop
            src={originalImage}
            crop={crop}
            onChange={(c) => setCrop(c)}
            onComplete={(c) => setCompletedCrop(c)}
            aspect={325 / 208}
            className="crop-image-container"
          >
            <img
              ref={imgRef}
              src={originalImage}
              alt="신분증 이미지"
              style={{
                maxWidth: "100%",
                // 하단 버튼 영역을 항상 남겨두되, 너무 작아지지 않게 최소 높이 확보
                maxHeight: "max(220px, min(70vh, calc(100dvh - 250px)))",
              }}
            />
          </ReactCrop>
        )}

        <canvas ref={previewCanvasRef} style={{ display: "none" }} />
      </div>

      <div className="crop-controls">
        <button className="crop-cancel-button" onClick={handleCancel}>
          취소
        </button>
        <button className="crop-complete-button" onClick={handleCropComplete}>
          완료
        </button>
      </div>

      <style jsx="true">{`
        .crop-page {
          display: flex;
          flex-direction: column;
          height: 100vh;
          height: 100dvh;
          min-height: 0;
          background-color: #0a0b0d;
          color: #fff;
          overflow: hidden;
        }

        .crop-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 14px 16px;
          background-color: #0a0b0d;
          position: relative;
          z-index: 10;
          flex-shrink: 0;
        }

        .crop-header h1 {
          font-size: 17px;
          font-weight: 600;
          margin: 0;
          text-align: center;
          flex-grow: 1;
        }

        .crop-back-button,
        .crop-close-button {
          background: none;
          border: none;
          cursor: pointer;
          color: white;
          padding: 5px;
        }

        .crop-container {
          flex: 1 1 auto;
          min-height: 0;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: flex-start;
          padding: 10px 10px 20px;
          overflow-y: auto;
          overflow-x: hidden;
          -webkit-overflow-scrolling: touch;
          overscroll-behavior: contain;
        }

        .crop-instructions {
          flex-shrink: 0;
          margin-bottom: 15px;
          text-align: center;
          color: #d6d8dc;
          font-size: 15px;
          font-weight: 500;
        }

        .crop-accent {
          color: #7a4fff;
          font-weight: 700;
        }

        .crop-controls {
          display: flex;
          justify-content: space-between;
          gap: 12px;
          padding: 16px 20px calc(28px + env(safe-area-inset-bottom, 0px));
          background-color: #0a0b0d;
          flex-shrink: 0;
          z-index: 10;
          border-top: 1px solid rgba(255, 255, 255, 0.06);
        }

        .crop-cancel-button,
        .crop-complete-button {
          padding: 15px 20px;
          border-radius: 12px;
          font-size: 16px;
          font-weight: 700;
          cursor: pointer;
          width: 48%;
        }

        .crop-cancel-button {
          background-color: #1c1f24;
          color: #fff;
          border: 1px solid rgba(255, 255, 255, 0.1);
        }

        .crop-complete-button {
          background-color: #7a4fff;
          color: #ffffff;
          border: none;
        }

        .crop-image-container {
          max-width: 100%;
          flex-shrink: 0;
          display: flex;
          justify-content: center;
        }

        .crop-image-container img {
          max-width: 100%;
          height: auto;
        }
      `}</style>
    </motion.div>
  );
}

export default IdCardCropPage;
